import type { Activity, ActivityLink, TipoLink } from '@/types'
import {
  formatInTimezone,
  getTimezoneAbbr,
  getLocalDate,
  getUserTimezone,
  getUserTimezoneAbbr,
  userMatchesVenueTimezone,
} from '@/lib/timezones'
import { titleWithEmoji } from '@/lib/activityEmoji'
import { cn } from '@/lib/utils'
import { ExternalLink, Plane, MapPin, Hotel, FileText } from 'lucide-react'
import type { ActivityStatus } from '@/lib/activityStatus'
import { formatTimeUntil } from '@/lib/activityStatus'
import { ActivityDetailDrawer } from './ActivityDetailDrawer'

const LINK_ICONS: Record<TipoLink, React.ReactNode> = {
  boarding_pass: <Plane className="w-3 h-3" />,
  maps: <MapPin className="w-3 h-3" />,
  hotel: <Hotel className="w-3 h-3" />,
  general: <FileText className="w-3 h-3" />,
}

interface Props {
  activity: Activity
  index?: number
  status?: ActivityStatus
  now?: Date
}

export function ActivityCard({ activity, index = 0, status = 'upcoming', now }: Props) {
  const startTime = formatInTimezone(activity.start_time_utc, activity.venue_timezone)
  const endTime = activity.end_time_utc
    ? formatInTimezone(activity.end_time_utc, activity.venue_timezone)
    : null
  const tzAbbr = getTimezoneAbbr(activity.venue_timezone)
  const endsNextDay = !!activity.end_time_utc &&
    getLocalDate(activity.end_time_utc, activity.venue_timezone) !==
    getLocalDate(activity.start_time_utc, activity.venue_timezone)

  const showLocalTime = !userMatchesVenueTimezone(activity.venue_timezone)
  const localStartTime = showLocalTime
    ? formatInTimezone(activity.start_time_utc, getUserTimezone())
    : null
  const localTzAbbr = showLocalTime ? getUserTimezoneAbbr() : null

  const isLive = status === 'live'
  const isNext = status === 'next'
  const isPast = status === 'past'
  const highlighted = isLive || isNext

  return (
    <ActivityDetailDrawer activity={activity} status={status} now={now}>
      <div
        className={cn('timeline-row', status)}
        style={{ '--i': index } as React.CSSProperties}
      >
        {/* Absolute time + tz label to the left of the rail */}
        <div className="t-time mono">
          <span>{startTime}</span>
          <span className="tz">{tzAbbr}</span>
        </div>

        {/* Dot on the rail — colored by status */}
        <div className="t-dot" aria-hidden="true" />

        {/* Card body */}
        <div className={cn('activity-card pressable', status)}>
          {/* Eyebrow: live / next chip + tz chip (only on highlighted states) */}
          {highlighted && (
            <div className="flex items-center justify-between mb-2">
              {isLive ? (
                <span className="chip chip-live">
                  <span className="dot" /> Live now
                </span>
              ) : (
                <span className="chip chip-primary">
                  Next{now ? ` · ${formatTimeUntil(activity.start_time_utc, now)}` : ''}
                </span>
              )}
              <span className="tz-chip mono">{tzAbbr}</span>
            </div>
          )}

          {/* Title row — tz chip inline when not highlighted */}
          {highlighted ? (
            <h3 className="font-semibold text-[15px] text-foreground leading-snug tracking-[-0.01em] m-0">
              {titleWithEmoji(activity.titulo)}
            </h3>
          ) : (
            <div className="flex items-start justify-between gap-2">
              <h3 className="font-semibold text-[15px] text-foreground leading-snug tracking-[-0.01em] m-0">
                {titleWithEmoji(activity.titulo)}
              </h3>
              <span className="tz-chip mono">{tzAbbr}</span>
            </div>
          )}

          {activity.venue_name && (
            <div className="inline-flex items-center gap-1.5 text-[13px] text-muted-foreground mt-1">
              <MapPin className="w-3 h-3 shrink-0" />
              <span>{activity.venue_name}</span>
            </div>
          )}

          {endTime && (
            <p className="text-xs text-[color:var(--subtle-foreground)] mt-1.5">
              Until {endTime}
              {endsNextDay && <span className="text-primary/80 ml-1">(next day)</span>}
            </p>
          )}

          {showLocalTime && localStartTime && (
            <p className="text-xs text-muted-foreground mt-1 tabular-nums">
              {localStartTime} {localTzAbbr} <span className="opacity-70">· your time</span>
            </p>
          )}

          {activity.descripcion && (
            <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2 mt-1.5">
              {activity.descripcion}
            </p>
          )}

          {activity.activity_links && activity.activity_links.length > 0 && (
            <div
              className="flex flex-wrap gap-1.5 mt-2.5"
              onClick={e => e.stopPropagation()}
            >
              {activity.activity_links.map(link => (
                <LinkChip key={link.id} link={link} accent={highlighted} />
              ))}
            </div>
          )}
        </div>
      </div>
    </ActivityDetailDrawer>
  )
}

function LinkChip({ link, accent }: { link: ActivityLink; accent: boolean }) {
  return (
    <a
      href={link.url}
      target="_blank"
      rel="noopener noreferrer"
      className={cn('link-chip', accent && 'link-primary')}
    >
      {LINK_ICONS[link.tipo]}
      {link.titulo}
      <ExternalLink className="w-2.5 h-2.5 opacity-60" />
    </a>
  )
}
